import { CommonModule } from '@angular/common';

import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FormBuilder, FormGroup } from '@angular/forms';

import { ActivatedRoute } from '@angular/router';

import { isFormValid } from '@app/helpers/form.helper';
import {
  getUserPrimaryAction,
  getUserPrimaryButtonLabelKey,
  getUserPrimaryHintKey,
  resolveProjectStatusIds,
  UserPrimaryActionKind,
} from '@app/helpers/project-state.helper';
import { WizardSection } from '@app/models/project-setup.models';

import { UploadService } from '@app/services/file/upload.service';

import { StepManagerService } from '@app/services/project-setup/step-manager.servive';

import { ProjectService } from '@app/services/projects/service';

import { ToastService } from '@app/services/toast.service';

import { PersonRoleStore } from '@app/stores/person-role/person-role.store';

import { ProjectSetupStore } from '@app/stores/project-setup/project-setup.store';

import { StepsStore } from '@app/stores/project-setup/steps/steps.store';

import { WizardStore } from '@app/stores/project-setup/wizard.store';

import { GenericProjectStore } from '@app/stores/projects/generic-project.store';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { EMPTY, finalize, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { BaseDataStepComponent } from 'src/app/components/project-wizard/base-data-step/base-data-step.component';

import {
  getProjectWizardFormGroup,
  ProjectWizardFormGroup,
} from 'src/app/components/project-wizard/project-wizard-form.helper';

import { SectionAComponent } from 'src/app/components/project-wizard/section-a/section-a.component';

import { SectionBComponent } from 'src/app/components/project-wizard/section-b/section-b.component';

import { SectionCComponent } from 'src/app/components/project-wizard/section-c/section-c.component';

import { SectionDComponent } from 'src/app/components/project-wizard/section-d/section-d.component';

import { SectionEComponent } from 'src/app/components/project-wizard/section-e/section-e.component';

import { SectionFComponent } from 'src/app/components/project-wizard/section-f/section-f.component';

import { SectionGComponent } from 'src/app/components/project-wizard/section-g/section-g.component';

import { SectionHComponent } from 'src/app/components/project-wizard/section-h/section-h.component';

import { SectionIComponent } from 'src/app/components/project-wizard/section-i/section-i.component';

import { ProjectWizardContactHelpComponent } from 'src/app/components/project-wizard/project-wizard-contact-help/project-wizard-contact-help.component';

import { SectionJComponent } from 'src/app/components/project-wizard/section-j/section-j.component';

import { WizardStepComponent } from 'src/app/components/wizard/wizard-step/wizard-step.component';

import {
  StepType,
  WizardComponent,
} from 'src/app/components/wizard/wizard.component';

@Component({
  selector: 'app-project-wizard',

  imports: [
    CommonModule,

    TranslateModule,

    WizardComponent,

    WizardStepComponent,

    SectionAComponent,

    SectionBComponent,

    SectionCComponent,

    SectionDComponent,

    SectionEComponent,

    SectionFComponent,

    SectionGComponent,

    SectionHComponent,

    SectionIComponent,

    SectionJComponent,

    BaseDataStepComponent,

    ProjectWizardContactHelpComponent,
  ],

  templateUrl: './project-wizard.component.html',

  styleUrls: ['./project-wizard.component.scss'],

  providers: [WizardStore, StepsStore, StepManagerService],
})
export class ProjectWizardComponent implements OnInit {
  projectSetupStore = inject(ProjectSetupStore);

  genericProjectStore = inject(GenericProjectStore);

  personRoleStore = inject(PersonRoleStore);

  protected stepsStore = inject(StepsStore);

  protected StepType = StepType;

  private stepManagerService = inject(StepManagerService);

  private wizardStore = inject(WizardStore);

  private readonly projectService = inject(ProjectService);

  private readonly toast = inject(ToastService);

  private readonly translate = inject(TranslateService);

  private perviousBtnTranslation = 'general.previous';

  protected readonly nextStepLabelKey = 'general.next';

  protected stepIndex = signal<number>(1);

  protected prevButtonText = signal<string>(this.perviousBtnTranslation);

  protected prevButtonHasChevron = signal<boolean>(false);

  protected nextButtonHasChevron = signal<boolean>(false);

  protected selectedProjectId = signal<string | null>(null);

  protected submittingForVerification = signal(false);

  protected wizardInitializing = signal(false);

  protected projectName = signal<string>('');

  protected contactUserId = signal<string | null>(null);

  protected projectState = signal<number | null>(null);

  protected readonly projectStatusIds = computed(() =>
    resolveProjectStatusIds(this.genericProjectStore.projectStatusEntities()),
  );

  protected readonly wizardStepsComplete = computed(() => {
    if (this.wizardInitializing()) {
      return false;
    }

    const steps = this.stepsStore.actualSteps();

    if (!steps.length) {
      return false;
    }

    return steps.every((step) => {
      if (
        step.stepType === StepType.disabled ||
        step.stepType === StepType.locked
      ) {
        return true;
      }

      return step.stepType === StepType.done;
    });
  });

  protected readonly userPrimaryButtonLabelKey = computed(() => {
    const k = getUserPrimaryButtonLabelKey(
      this.projectState(),
      this.projectStatusIds(),
    );

    return k === 'submitForReview'
      ? 'projectWizard.primaryAction.submitForReview'
      : 'projectWizard.primaryAction.publish';
  });

  protected readonly userPrimaryHintKey = computed(() =>
    getUserPrimaryHintKey(
      this.projectState(),
      this.wizardStepsComplete(),
      this.projectStatusIds(),
    ),
  );

  protected readonly userPrimaryTooltipKey = computed(() => {
    if (this.submittingForVerification()) {
      return null;
    }
    if (this.canRunUserPrimaryAction()) {
      return null;
    }
    return this.userPrimaryHintKey();
  });

  protected readonly canRunUserPrimaryAction = computed(() => {
    const action = getUserPrimaryAction(this.projectState(), this.projectStatusIds());
    if (!action) {
      return false;
    }
    if (
      action === 'publish' &&
      this.projectState() === this.projectStatusIds().PublishReady
    ) {
      return true;
    }
    return this.wizardStepsComplete();
  });

  protected readonly lastWizardStepIndex = computed(() =>
    Math.max(0, this.stepsStore.actualSteps().length - 1),
  );

  protected readonly isOnLastWizardStep = computed(
    () => this.stepsStore.stepIndex() === this.lastWizardStepIndex(),
  );

  protected readonly canRunSectionJFooterSubmit = computed(() => {
    if (this.wizardInitializing()) {
      return false;
    }
    if (this.submittingForVerification()) {
      return false;
    }
    if (!this.canEditWizard()) {
      return false;
    }
    if (!getUserPrimaryAction(this.projectState(), this.projectStatusIds())) {
      return false;
    }
    const steps = this.stepsStore.actualSteps();
    const lastIdx = this.lastWizardStepIndex();
    for (let i = 0; i < lastIdx; i++) {
      const step = steps[i];
      if (
        step.stepType === StepType.disabled ||
        step.stepType === StepType.locked
      ) {
        continue;
      }
      if (step.stepType !== StepType.done) {
        return false;
      }
    }
    const jForm = this.wizardStore.getSectionForm(WizardSection.J);
    return jForm !== null && isFormValid(jForm);
  });

  protected readonly wizardNextFooterEnabled = computed(() => {
    if (!this.isOnLastWizardStep()) {
      return true;
    }
    return this.canRunSectionJFooterSubmit();
  });

  protected readonly sectionJFooterTooltipKey = computed(() => {
    if (!this.isOnLastWizardStep()) {
      return null;
    }
    if (this.submittingForVerification()) {
      return null;
    }
    if (this.canRunSectionJFooterSubmit()) {
      return null;
    }
    return getUserPrimaryHintKey(
      this.projectState(),
      false,
      this.projectStatusIds(),
    );
  });

  protected readonly canEditWizard = computed(() => {
    const state = this.projectState();
    const statusIds = this.projectStatusIds();
    return state === statusIds.Draft || state === statusIds.DataSupplyNeeded;
  });

  projectWizardFormGroup: FormGroup<ProjectWizardFormGroup>;

  constructor(
    private fb: FormBuilder,

    private destroyRef: DestroyRef,

    private fileUploadService: UploadService,

    private route: ActivatedRoute,
  ) {
    this.projectWizardFormGroup = getProjectWizardFormGroup(fb);

    this.selectedProjectId.set(this.route.snapshot.paramMap.get('id'));
  }

  ngOnInit(): void {
    const id = this.selectedProjectId();

    if (!id) {
      return;
    }

    this.wizardInitializing.set(true);

    this.stepManagerService

      .initializeStepsFromData(id)

      .pipe(
        takeUntilDestroyed(this.destroyRef),

        finalize(() => this.wizardInitializing.set(false)),
      )

      .subscribe({
        next: (data) => {
          this.projectName.set(data.projectName);

          this.contactUserId.set(data.contactUserId);

          this.projectState.set(data.state);
          this.applyReadOnlyWizardState();
        },
      });
  }

  onStepSelection(index: number) {
    this.stepManagerService.onStepClick(index);
  }

  protected onPreviousButtonClick(): void {
    this.stepManagerService.onPreviousClick();
  }

  protected onNextButtonClick() {
    if (!this.canEditWizard()) {
      this.stepManagerService.onNextReadOnlyClick();
      return;
    }

    const id = this.selectedProjectId()?.trim();
    if (!id) {
      return;
    }

    if (!this.isOnLastWizardStep()) {
      this.stepManagerService.onNextClick(id);
      return;
    }

    if (!this.canRunSectionJFooterSubmit()) {
      return;
    }

    const action = getUserPrimaryAction(
      this.projectState(),
      this.projectStatusIds(),
    );
    if (!action) {
      return;
    }

    this.submittingForVerification.set(true);

    this.stepManagerService
      .saveSectionJWithUploads(id)
      .pipe(
        switchMap(() => this.executeUserPrimaryStateTransition$(id)),
        finalize(() => this.submittingForVerification.set(false)),
      )
      .subscribe({
        next: () => {
          this.handlePrimaryActionSuccess(action);
        },
        error: () => {
          this.toast.error(
            this.translate.instant('projectWizard.primaryAction.failed'),
          );
        },
      });
  }

  onUserPrimaryAction(): void {
    if (!this.canRunUserPrimaryAction()) {
      return;
    }

    const id = this.selectedProjectId()?.trim();

    if (!id) {
      return;
    }

    const action = getUserPrimaryAction(
      this.projectState(),
      this.projectStatusIds(),
    );

    if (!action) {
      return;
    }

    this.submittingForVerification.set(true);

    this.stepManagerService
      .saveCurrentSectionIfNeeded(id)
      .pipe(switchMap(() => this.executeUserPrimaryStateTransition$(id)))
      .pipe(finalize(() => this.submittingForVerification.set(false)))
      .subscribe({
        next: () => {
          this.handlePrimaryActionSuccess(action);
        },
        error: () => {
          this.toast.error(
            this.translate.instant('projectWizard.primaryAction.failed'),
          );
        },
      });
  }

  protected onSaveDraftClick(): void {
    if (!this.canEditWizard()) {
      return;
    }

    const id = this.selectedProjectId()?.trim();
    if (!id) {
      return;
    }

    this.stepManagerService.saveCurrentSectionIfNeeded(id).subscribe();
  }

  private executeUserPrimaryStateTransition$(id: string): Observable<void> {
    const action = getUserPrimaryAction(
      this.projectState(),
      this.projectStatusIds(),
    );
    if (!action) {
      return EMPTY;
    }
    const request$ =
      action === 'submitForReview'
        ? this.projectService.updateProjectStateToStaffAnalyzeStarted(id)
        : this.projectService.updateProjectStateToPublished(id);
    return request$.pipe(map(() => undefined));
  }

  private handlePrimaryActionSuccess(action: UserPrimaryActionKind): void {
    this.toast.success(
      this.translate.instant(
        action === 'submitForReview'
          ? 'projectWizard.primaryAction.successSubmit'
          : 'projectWizard.primaryAction.successPublish',
      ),
    );
    const state = this.projectState();
    const statusIds = this.projectStatusIds();
    if (action === 'publish') {
      this.projectState.set(statusIds.Published);
      this.applyReadOnlyWizardState();
      return;
    }
    if (state === statusIds.Draft) {
      this.projectState.set(statusIds.AnalyseStartedByStaff);
      this.applyReadOnlyWizardState();
    }
  }

  private applyReadOnlyWizardState(): void {
    if (this.canEditWizard()) {
      return;
    }

    this.lockWizardForms();
    this.lockWizardSteps();
  }

  private lockWizardForms(): void {
    const wizardSections: Exclude<WizardSection, WizardSection.Base>[] = [
      WizardSection.A,
      WizardSection.B,
      WizardSection.C,
      WizardSection.D,
      WizardSection.E,
      WizardSection.F,
      WizardSection.G,
      WizardSection.H,
      WizardSection.I,
      WizardSection.J,
    ];

    wizardSections.forEach((section) => {
      this.wizardStore.getSectionForm(section)?.disable({ emitEvent: false });
    });
  }

  private lockWizardSteps(): void {
    const updates = this.stepsStore
      .actualSteps()
      .map((step, index) =>
        step.stepType === StepType.disabled
          ? null
          : { index, status: StepType.locked },
      )
      .filter((update): update is { index: number; status: StepType } => update !== null);

    if (updates.length > 0) {
      this.stepsStore.batchUpdateStepStatus(updates);
    }
  }
}
